/*
 * Revenue Projection — used by the CXO and Finance panels.
 * Same two line charts (Average Sale, Total Sale per month) for Edutech or Publication.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import moment from "moment";
import { CategoryScale, Chart as ChartJS, Legend, LineElement, LinearScale, PointElement, Title, Tooltip } from "chart.js";
import { Line } from "react-chartjs-2";
import { CalendarDays, IndianRupee, TrendingUp } from "lucide-react";
import { getMonthlyRevenue, getTransactionForecast } from "../../Action/courseAction";
import { CustomDropdowns, CustomPageLayout, CustomStatCard } from "../../Component/customComponents";
import { formatDateRange } from "../Common/transactionCommon";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const COMPANY_TYPES = [
  { _id: 1, name: "Edutech", key: "edutech" },
  { _id: 2, name: "Publication", key: "publication" },
];
// the API sends "NaN" as text when there is nothing to average (same handling as before)
const rupees = (v) => `₹ ${v && v !== "NaN" ? v : 0}`;

const lineData = (labels, label, data) => ({
  labels,
  datasets: [{ label, data, borderColor: "rgb(53, 162, 235)", backgroundColor: "rgba(53, 162, 235, 0.5)", tension: 0.4 }],
});
const OPTIONS = { responsive: true, plugins: { legend: { position: "top" }, title: { display: true, text: "" } } };

function RevenueProjection() {
  const dispatch = useDispatch();
  const [company, setCompany] = useState(COMPANY_TYPES[0]);
  const [forecast, setForecast] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [loading, setLoading] = useState(true);

  // the period was fixed to this month (the old date pickers were disabled)
  const startOfMonth = moment().startOf("month").format("YYYY-MM-DD");
  const endOfMonth = moment().endOf("month").format("YYYY-MM-DD");

  useEffect(() => {
    let pending = 2;
    const done = () => (pending -= 1) <= 0 && setLoading(false);
    dispatch(
      getTransactionForecast(
        (res) => {
          setForecast(Array.isArray(res) && res.length ? res[0] : null);
          done();
        },
        done,
      ),
    );
    dispatch(
      getMonthlyRevenue(
        (res) => {
          setMonthly(Array.isArray(res) ? res : []);
          done();
        },
        done,
      ),
    );
  }, []);

  const selected = forecast?.[company.key] || {};
  const charts = useMemo(() => {
    const labels = monthly.map((m) => m?.monthYear);
    return {
      avg: lineData(labels, "Average Sale", monthly.map((m) => m?.[company.key]?.avgSales)),
      total: lineData(labels, "Total Sale", monthly.map((m) => m?.[company.key]?.totalSale)),
    };
  }, [monthly, company]);

  return (
    <CustomPageLayout title="REVENUE PROJECTION">
      <div className="d-flex flex-column gap-3">
        <section className="bg-white border rounded-3 shadow-sm p-3">
          <div className="row g-3 align-items-end">
            <div className="col-md-4 col-12">
              <CustomDropdowns label="Company Type" options={COMPANY_TYPES} value={company} onChange={(v) => v && setCompany(v)} search={false} clearable={false} />
            </div>
            <div className="col-md-8 col-12 text-md-end small text-muted">
              <CalendarDays size={14} className="me-1" />
              Period: {formatDateRange(startOfMonth, endOfMonth)}
            </div>
          </div>
        </section>

        <div className="row g-3">
          <CustomStatCard colClass="col-md-6 col-12" title="Average Sale" value={rupees(selected.avgSales)} subtitle={company.name} icon={<IndianRupee size={18} />} tone="primary" loading={loading} />
          <CustomStatCard colClass="col-md-6 col-12" title="Sale Forecast" value={rupees(selected.saleForecast)} subtitle={`${company.name} · this month`} icon={<TrendingUp size={18} />} tone="success" loading={loading} />
        </div>

        {/* same two line charts */}
        <div className="row g-3">
          <div className="col-xl-6 col-12">
            <section className="bg-white border rounded-3 shadow-sm p-3 h-100">
              {monthly.length ? <Line options={OPTIONS} data={charts.avg} /> : <div className="text-center text-muted py-5">{loading ? "Loading chart…" : "No monthly revenue yet."}</div>}
            </section>
          </div>
          <div className="col-xl-6 col-12">
            <section className="bg-white border rounded-3 shadow-sm p-3 h-100">
              {monthly.length ? <Line options={OPTIONS} data={charts.total} /> : <div className="text-center text-muted py-5">{loading ? "Loading chart…" : "No monthly revenue yet."}</div>}
            </section>
          </div>
        </div>
      </div>
    </CustomPageLayout>
  );
}

export default RevenueProjection;
